import { animated } from '@react-spring/web';
import { FormEvent, useMemo, useState } from 'react';
import { ButtonAlign } from '../Button/consts';
import ButtonSubmit from '../ButtonSubmit/ButtonSubmit';
import { ButtonSubmitState } from '../ButtonSubmit/consts';
import Stack from '../Stack/Stack';
import CourseEventList from './CourseEventList/CourseRadioButtonList';
import CourseEventQuarterHeader from './CourseEventQuarterHeader/CourseEventQuarterHeader';
import CourseEventQuarterNavButton from './CourseEventQuarterNavButton/CourseEventQuarterNavButton';
import styles from './CourseTimetable.module.scss';
import { dateToYearQuarter, yearQuarterToTitle } from './helpers';
import { useQuarter } from './hooks';
import type { CourseTimetableProps } from './types';

const CourseTimetable = ({
  events,
  onSubmit,
  children,
}: CourseTimetableProps) => {
  const [selectedEventID, setSelectedEventID] = useState('');

  const {
    nextQuarter,
    prevQuarter,
    canNextQuarter,
    canPrevQuarter,
    quarterTransitions,
    quarterList,
    quarter,
  } = useQuarter();

  const quarterEvents = useMemo(
    () =>
      quarterList.map((yearQuarter) => {
        return events
          .filter(
            ({ startDate }) => dateToYearQuarter(startDate) === yearQuarter
          )
          .sort((a, b) => a.startDate.getTime() - b.startDate.getTime());
      }),
    [events, quarterList]
  );

  const handleSubmit = ($event: FormEvent) => {
    $event?.preventDefault();
    onSubmit(selectedEventID);
  };

  const onChangeRadioButton = (value: string) => setSelectedEventID(value);

  return (
    <form className={styles.base} onSubmit={handleSubmit}>
      <div className={styles.mobileCarousel}>
        <CourseEventQuarterHeader>
          <CourseEventQuarterNavButton
            disabled={!canPrevQuarter}
            onClick={prevQuarter}
            variant="prev"
          />

          <span>{yearQuarterToTitle(quarter)}</span>

          <CourseEventQuarterNavButton
            disabled={!canNextQuarter}
            onClick={nextQuarter}
            variant="next"
          />
        </CourseEventQuarterHeader>

        {quarterTransitions((props, quarterIndex) => (
          <animated.div style={props} key={quarterIndex}>
            <CourseEventList
              onChangeRadioButton={onChangeRadioButton}
              selectedEventID={selectedEventID}
              events={quarterEvents[quarterIndex]}
              fieldName="selectedEventID"
            />
          </animated.div>
        ))}
      </div>

      <div className={styles.grid}>
        {quarterEvents.map((events, quarterIndex) => {
          const yearQuarter = quarterList[quarterIndex];
          return (
            <div className={styles.quarterWrapper} key={yearQuarter}>
              <CourseEventQuarterHeader>
                {yearQuarterToTitle(yearQuarter)}
              </CourseEventQuarterHeader>

              <CourseEventList
                onChangeRadioButton={onChangeRadioButton}
                selectedEventID={selectedEventID}
                events={events}
                fieldName="selectedEventID"
              />
            </div>
          );
        })}
      </div>

      <Stack gap="1rem" alignItems="center">
        <ButtonSubmit
          state={ButtonSubmitState.Initial}
          align={ButtonAlign.Center}
          inactive={!selectedEventID}
        >
          Weiter zur Anmeldung
        </ButtonSubmit>

        {children}
      </Stack>
    </form>
  );
};

export default CourseTimetable;
